/**
 * Brand-owner payment configuration card.
 *
 * Mirrors the SendGrid card on the Mail Manager page: keys go to the
 * server encrypted, only redacted previews come back, and there's an
 * opt-in switch that decides whether the brand's keys are actually used
 * for checkout. When the switch is off the platform's Stripe handles
 * payment (so checkout still works while the brand sets up Stripe).
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

// Inline `<Code>` substitute — MUI doesn't ship one, and Typography's
// `code` variant maps to `<code>` but without the styling we want.
const Code = ({ children, sx }) => (
  <Box
    component='code'
    sx={{
      fontFamily: 'monospace',
      fontSize: '0.85em',
      bgcolor: 'action.hover',
      px: '6px', py: '2px',
      borderRadius: '4px',
      wordBreak: 'break-all',
      ...sx,
    }}
  >
    {children}
  </Box>
);

export default function PaymentsConfig({ brandData }) {
  const brandId = brandData?._id;
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    if (!brandId) return;
    setLoading(true);
    API.getBrandPaymentConfig({ brand_id: brandId })
      .then((res) => {
        setConfig(res);
        setForm({
          mode: res?.mode || 'test',
          use_own_stripe: !!res?.use_own_stripe,
          stripe_publishable_key: res?.stripe_publishable_key || '',
          stripe_secret_key: '', // never echoed
          stripe_webhook_secret: '', // never echoed
        });
      })
      .catch((err) => toast.error(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [brandId]);

  const save = () => {
    setSaving(true);
    const patch = {
      mode: form.mode,
      use_own_stripe: form.use_own_stripe,
      stripe_publishable_key: form.stripe_publishable_key,
    };
    if (form.stripe_secret_key) patch.stripe_secret_key = form.stripe_secret_key;
    if (form.stripe_webhook_secret) patch.stripe_webhook_secret = form.stripe_webhook_secret;
    API.upsertBrandPaymentConfig({ brand_id: brandId, patch })
      .then(() => { toast.success('Saved'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  const clearKey = (which) => {
    if (!window.confirm(`Remove the stored ${which === 'webhook' ? 'webhook secret' : 'API key'}?`)) return;
    API.upsertBrandPaymentConfig({
      brand_id: brandId,
      patch: which === 'webhook' ? { clear_webhook_secret: true } : { clear_secret_key: true },
    })
      .then(() => { toast.success('Removed'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  if (!brandId) return null;

  const webhookUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/v1/public/storefront/stripe-webhook?brand_id=${brandId}`
    : `/api/v1/public/storefront/stripe-webhook?brand_id=${brandId}`;

  return (
    <Card sx={{ mb: '20px', borderRadius: '20px' }}>
      <CardContent>
        <Box mb='14px' ml='10px'>
          <Typography variant='h6' fontWeight='bold'>Payments (Stripe)</Typography>
          <Typography variant='body2' color='text.secondary'>
            Process checkout under your own Stripe account. Funds settle directly into your bank; refunds and disputes happen on your Stripe dashboard.
          </Typography>
        </Box>

        <Box mx='10px'>
          {loading && <Typography variant='body2' color='text.secondary'>Loading…</Typography>}

          <Stack spacing='12px'>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px'>
              <FormControl size='small' sx={{ maxWidth: 160 }}>
                <InputLabel>Mode</InputLabel>
                <Select
                  label='Mode'
                  value={form.mode || 'test'}
                  onChange={(e) => setForm({ ...form, mode: e.target.value })}
                >
                  <MenuItem value='test'>Test</MenuItem>
                  <MenuItem value='live'>Live</MenuItem>
                </Select>
              </FormControl>
              <Box flex='2'>
                <TextField
                  fullWidth
                  size='small'
                  label='Publishable key'
                  value={form.stripe_publishable_key || ''}
                  onChange={(e) => setForm({ ...form, stripe_publishable_key: e.target.value })}
                  placeholder='pk_test_… or pk_live_…'
                  helperText="Public-by-design: shown to customers' browsers on checkout. Not encrypted."
                />
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px' alignItems={{ md: 'flex-start' }}>
              <Box flex='2'>
                <TextField
                  fullWidth
                  size='small'
                  type='password'
                  label='Secret API key'
                  value={form.stripe_secret_key || ''}
                  placeholder={config?.has_secret_key ? `Stored — ${config.redacted_secret_key}` : 'sk_test_… or sk_live_…'}
                  onChange={(e) => setForm({ ...form, stripe_secret_key: e.target.value })}
                  helperText='Stored AES-256-GCM encrypted. Never echoed back to the browser.'
                />
              </Box>
              {config?.has_secret_key && (
                <Button variant='outlined' color='error' onClick={() => clearKey('secret')} sx={{ mt: { md: '0' } }}>
                  Remove key
                </Button>
              )}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px' alignItems={{ md: 'flex-start' }}>
              <Box flex='2'>
                <TextField
                  fullWidth
                  size='small'
                  type='password'
                  label='Webhook signing secret'
                  value={form.stripe_webhook_secret || ''}
                  placeholder={config?.has_webhook_secret ? `Stored — ${config.redacted_webhook_secret}` : 'whsec_…'}
                  onChange={(e) => setForm({ ...form, stripe_webhook_secret: e.target.value })}
                />
              </Box>
              {config?.has_webhook_secret && (
                <Button variant='outlined' color='error' onClick={() => clearKey('webhook')}>
                  Remove
                </Button>
              )}
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={!!form.use_own_stripe}
                  onChange={(e) => setForm({ ...form, use_own_stripe: e.target.checked })}
                />
              }
              label="Use this brand's Stripe account for checkout"
            />

            <Divider />

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                p: '12px',
                bgcolor: 'action.hover',
                fontSize: '0.875rem',
              }}
            >
              <Typography fontWeight='600' mb='6px'>Set this webhook in Stripe</Typography>
              <Typography variant='body2' color='text.secondary' mb='6px'>
                In your Stripe dashboard → Developers → Webhooks, add an endpoint pointing to:
              </Typography>
              <Code sx={{ display: 'block', p: '8px', borderRadius: '8px' }}>
                {webhookUrl}
              </Code>
              <Typography variant='body2' color='text.secondary' mt='8px'>
                Subscribe to <Code>checkout.session.completed</Code> and <Code>payment_intent.succeeded</Code>.
                Copy the signing secret Stripe gives you back into the field above.
              </Typography>
            </Box>

            <Stack direction='row' justifyContent='flex-end' pt='4px'>
              <Button variant='contained' onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
